import { useTranslation } from "react-i18next";
import {  useSelector} from "react-redux";
import { Card, Col, Row } from "react-bootstrap";
import { RootState } from "../../../../redux/store";
import { AuditActionEnum } from "../../../auditLogs/types";
import { generateCsvFile } from "../../../../utils/downloadCsvFile";
import { useClaimIndicatorsTimeTracking } from "../../hooks/useClaimIndicatorsTimeTracking";
import "./ClaimIndicatorsTimeTracking.css"
import ChartShareActions from "../../../../components/ChartShareActions";
import { useRef } from "react";
import { ClaimIndicatorTimeTrackingDto } from "../../types";

export function ClaimIndicatorsTimeTracking() {
    const { t } = useTranslation()
    const { data } = useClaimIndicatorsTimeTracking();
    const indicatorTimeTrackingRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.claims.selectedFilters);

    const exportToCSV = async (): Promise<void> => {
        const rows: ClaimIndicatorTimeTrackingDto[] = data ? [data] : [];
        await generateCsvFile({
            headers: [
                t("claim:indicatorsTimeTracking.eventDateToEntryDate"),
                t("claim:indicatorsTimeTracking.entryDateToInternalReportRequestDate"),
                t("claim:indicatorsTimeTracking.internalReportRequestDateToInternalReportDate"),
                t("claim:indicatorsTimeTracking.internalReportDateToDeliveryDate"),
                t("claim:indicatorsTimeTracking.entryDateToDeliveryDate"),
                t("claim:indicatorsTimeTracking.entryDateToClosingDate"),
            ],
            rows,
            mapRow: (item) => [
                item.eventDateToEntryDate ?? 0,
                item.entryDateToInternalReportRequestDate ?? 0,
                item.internalReportRequestDateToInternalReportDate ?? 0,
                item.internalReportDateToDeliveryDate ?? 0,
                item.entryDateToDeliveryDate ?? 0,
                item.entryDateToClosingDate ?? 0,
            ],
            fileBaseName: "claims_indicator_time_tracking",
            trailingSemicolon: true,
            audit: {
                entity: "DashboardIndicator",
                action: AuditActionEnum.ExportCsv,
                source: window.location.pathname,
                description: "CSV export of claim status timeline",
                filters: selectedFilters
            },

        });
    };

    return (
        <div ref={indicatorTimeTrackingRef}>
            <Card className="p-3 main-indicator-card mt-2">
                <Row className="align-items-center justify-content-between mb-3 w-100">
                    <Col xs="auto">
                        <h4 className="mb-0">
                            {t("claim:indicatorsTimeTracking.title")}
                        </h4>
                    </Col>

                    <Col xs="auto">
                        <ChartShareActions
                            chartRef={indicatorTimeTrackingRef}
                            title={t("claim:indicatorsTimeTracking.title")}
                            filters={selectedFilters}
                            t={t}
                            exportCsvAction={exportToCSV}
                        />
                    </Col>
                </Row>
                <Row className="g-3 w-100">
                    <Col md={2}>
                        <Card className="p-3 h-100 border-0 shadow-sm">
                            <div className="d-flex align-items-center gap-3">

                                <div className="indicator-bar-deep-blue" />

                                <div>
                                    <p className="text-muted mb-1 small">
                                        {t("claim:indicatorsTimeTracking.eventDateToEntryDate")}
                                    </p>

                                    <h6 className="mb-0"> {t("claim:indicatorsTimeTracking.days", { days: data?.eventDateToEntryDate || "-" })}</h6>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col md={2}>
                        <Card className="p-3 h-100 border-0 shadow-sm">
                            <div className="d-flex align-items-center gap-3">

                                <div className="indicator-bar-brilliant-blue" />

                                <div>
                                    <p className="text-muted mb-1 small">
                                        {t("claim:indicatorsTimeTracking.entryDateToInternalReportRequestDate")}
                                    </p>

                                    <h6 className="mb-0">{t("claim:indicatorsTimeTracking.days", { days: data?.entryDateToInternalReportRequestDate || "-" })}</h6>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col md={2}>
                        <Card className="p-3 h-100 border-0 shadow-sm">
                            <div className="d-flex align-items-center gap-3">

                                <div className="indicator-bar-cyan" />

                                <div>
                                    <p className="text-muted mb-1 small">
                                        {t("claim:indicatorsTimeTracking.internalReportRequestDateToInternalReportDate")}
                                    </p>

                                    <h6 className="mb-0">{t("claim:indicatorsTimeTracking.days", { days: data?.internalReportRequestDateToInternalReportDate || "-" })}</h6>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="p-3 h-100 border-0 shadow-sm">
                            <div className="d-flex align-items-center gap-3">

                                <div className="indicator-bar-lavender" />

                                <div>
                                    <p className="text-muted mb-1 small">
                                        {t("claim:indicatorsTimeTracking.internalReportDateToDeliveryDate")}
                                    </p>

                                    <h6 className="mb-0">{t("claim:indicatorsTimeTracking.days", { days: data?.internalReportDateToDeliveryDate || "-" })}</h6>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="p-3 h-100 border-0 shadow-sm">
                            <div className="d-flex align-items-center gap-3">

                                <div className="indicator-bar-mint-green" />

                                <div>
                                    <p className="text-muted mb-1 small">
                                        {t("claim:indicatorsTimeTracking.entryDateToDeliveryDate")}
                                    </p>

                                    <h6 className="mb-0">{t("claim:indicatorsTimeTracking.days", { days: data?.entryDateToDeliveryDate || "-" })}</h6>
                                </div>
                            </div>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="p-3 h-100 border-0 shadow-sm">
                            <div className="d-flex align-items-center gap-3">

                                <div className="indicator-bar-moss-green" />

                                <div>
                                    <p className="text-muted mb-1 small">
                                        {t("claim:indicatorsTimeTracking.entryDateToClosingDate")}
                                    </p>

                                    <h6 className="mb-0">{t("claim:indicatorsTimeTracking.days", { days: data?.entryDateToClosingDate || "-" })}</h6>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Card >
        </div>

    );
}